import * as React from "react"

import { cn } from "@/lib/utils"

interface SegmentedFilterOption<TValue extends string> {
  value: TValue
  label: React.ReactNode
}

interface SegmentedFilterProps<TValue extends string> {
  value: TValue
  options: readonly SegmentedFilterOption<TValue>[]
  onValueChange: (value: TValue) => void
  className?: string
  buttonClassName?: string
  activeButtonClassName?: string
  inactiveButtonClassName?: string
  "aria-label"?: string
}

const SegmentedFilter = <TValue extends string>({
  value,
  options,
  onValueChange,
  className,
  buttonClassName,
  activeButtonClassName,
  inactiveButtonClassName,
  "aria-label": ariaLabel,
}: SegmentedFilterProps<TValue>) => (
  <div className={cn("flex", className)} role="group" aria-label={ariaLabel}>
    {options.map((option, index) => {
      const isActive = value === option.value

      return (
        <button
          key={option.value}
          type="button"
          aria-pressed={isActive}
          onClick={() => onValueChange(option.value)}
          className={cn(
            "border border-border px-4 py-2 text-xs font-bold uppercase tracking-widest transition-colors",
            index > 0 && "-ml-px",
            isActive
              ? cn(
                  "relative z-10 border-primary bg-primary text-primary-foreground",
                  activeButtonClassName
                )
              : cn(
                  "bg-background text-muted-foreground hover:border-primary/50 hover:text-foreground",
                  inactiveButtonClassName
                ),
            buttonClassName
          )}
        >
          {option.label}
        </button>
      )
    })}
  </div>
)

export { SegmentedFilter, type SegmentedFilterOption }
